import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, Dropdown, Menu, Card, Empty, Flex } from 'antd';
import { SearchOutlined, LeftOutlined, RightOutlined, MoreOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { useQuery, useLazyQuery } from '@apollo/client';
import { Get_Account_Experts, Get_Expert_Video_By_Id, Get_Content_In_Other_Langauages } from '../../../../../services/userAPI';
import './contentLibrary.css';
import { useLocation, useNavigate } from 'react-router-dom';
import Star from "../../../../../Images/starIcon.png";
import TranslateIcon from '@mui/icons-material/Translate';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import editIcon from '../../../../../Images/editIcon.png';
import Arrow from "../../../../../Images/contentArrow.png";

// Separated Video Card component for better state isolation
const VideoCard = ({ video, expert, menuItems, activeDropdown, setActiveDropdown }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const titleRef = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

    const isTruncated = video.title?.length > 60;
    const duration = formatDuration(video.videoLengthMins || 0, video.videoLengthSecs || 0);

    const handleTitleHover = () => {
        if (video.title?.length > 60 && titleRef.current) {
            const rect = titleRef.current.getBoundingClientRect();
            setTooltipPosition({
                top: rect.bottom + 5,
                left: rect.left
            });
            setShowTooltip(true);
        }
    };

    const toggleExpansion = (e) => {
        e.stopPropagation();
        setIsExpanded((prev) => !prev);
    };

    return (
        <div className={`video-card ${isExpanded ? "video-card-expanded" : ""}`}
        >
            <div
                className="video-thumbnail"
                style={{
                    backgroundImage: `url(${video.vimeoThumbnailUrl || '/api/placeholder/320/180'})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            >
                <div className="video-duration">{duration}</div>
                <div className="play-button">
                    <div className="play-icon"></div>
                </div>
            </div>
            {video.originalContent === true ? (
                <div className="original-label">
                    Original
                </div>
            ) : video.originalContent === false ? (
                <div className="original-placeholder"></div>
            ) : null}
            <div className="video-details">
                <h3
                    ref={titleRef}
                    className="video-title"
                    onMouseEnter={handleTitleHover}
                    onMouseLeave={() => setShowTooltip(false)}
                >
                    {isTruncated ? video.title.substring(0, 60) + "..." : video.title}
                </h3>
                {showTooltip && (
                    <div
                        className="tooltip"
                        style={{
                            position: 'fixed',
                            top: `${tooltipPosition.top}px`,
                            left: `${tooltipPosition.left}px`,
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                            zIndex: 1000,
                            maxWidth: '300px',
                            wordWrap: 'break-word'
                        }}
                    >
                        {video.title}
                    </div>
                )}
                <div className="author-row">
                    <img
                        className="author-avatar"
                        src={Star}
                        alt={expert.name?.charAt(0) || '?'}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = "none";
                        }}
                    />
                    <span className="author-name">{expert.name || 'Unknown Expert'}</span>
                    <Dropdown
                        overlay={menuItems(video.id)}
                        trigger={['click']}
                        open={activeDropdown === video.id}
                        onOpenChange={(visible) => {
                            setActiveDropdown(visible ? video.id : null);
                        }}
                    >
                        <Button
                            className="more-button"
                            icon={<MoreOutlined />}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </Dropdown>
                </div>
                <div className="video-tags">
                    <span className="language-tag" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <TranslateIcon style={{ width: '12px', height: '12px' }} />
                        {video.language || 'N/A'}
                    </span>
                    <span className="category-tag" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <CategoryOutlinedIcon style={{ width: '12px', height: '12px' }} />
                        {video.fourmatCategory || 'N/A'}
                    </span>
                </div>
                <p className="video-description">
                    {isExpanded ? video.description : video.description?.substring(0, 55) + "..."}
                </p>
                <div className="view-more-container">
                    <button
                        type="button"
                        className="view-more-button"
                        onClick={toggleExpansion}
                        onKeyDown={(e) => e.key === 'Enter' && toggleExpansion(e)}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? "Collapse video details" : "Expand video details"}
                    >
                        {isExpanded ? "View Less" : "View More"}
                        <span className={`view-more-arrow ${isExpanded ? "expanded" : ""}`}>
                            {isExpanded ? <UpOutlined /> : <DownOutlined />}
                        </span>
                    </button>
                </div>
                {isExpanded && (
                    <div className="metadata-dropdown">
                        <div className="metadata-section">
                            <div className="metadata-header">Activity</div>
                            <div className="metadata-value">{video.activityName || 'N/A'}</div>
                        </div>
                        <div className="metadata-section">
                            <div className="metadata-header">Task</div>
                            <div className="metadata-value">{video.taskName || 'N/A'}</div>
                        </div>
                        <div className="metadata-section">
                            <div className="metadata-header">Sub-Task</div>
                            <div className="metadata-value">{video.subtaskName || 'N/A'}</div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Function to format video duration
function formatDuration(mins, secs) {
    const totalSeconds = (mins * 60) + secs;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

const ContentLibrary = () => {
    const location = useLocation();
    const accountId = location.state?.accountId;
    const roleId = location.state?.roleId;
    const accountName = location.state?.accountName;
    const name = location.state?.name;
    console.log("ContentLibrary received state:", { accountId });
    const navigate = useNavigate();

    const [expertPagination, setExpertPagination] = useState({});
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [experts, setExperts] = useState([]);
    const [expertVideos, setExpertVideos] = useState({});
    const [expertVideoCounts, setExpertVideoCounts] = useState({});
    const totalVideosCount = Object.values(expertVideoCounts).reduce(
        (count, videoCount) => count + videoCount,
        0
    );
    const limit = 5;
    const sortByField = 'created_at';
    const sortOrder = 'desc';
    const [fetchExperts, { loading: expertsLoading, error: expertsError, data: expertsData }] = useLazyQuery(Get_Account_Experts, {
        fetchPolicy: 'network-only',
        onError: (error) => {
            console.error('Error fetching experts:', error);
        },
    });
    const isVideoCountLoading = expertsLoading ||
        (experts.length > 0 && Object.keys(expertVideoCounts).length < experts.length);

    const [getExpertVideo] = useLazyQuery(Get_Expert_Video_By_Id, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            console.log('Get_Expert_Video_By_Id response:', data);
        },
        onError: (error) => {
            console.error('Get_Expert_Video_By_Id error:', error);
        }
    });

    const [getContentInOtherLanguages, { loading: otherLanguagesLoading, error: otherLanguagesError }] = useLazyQuery(Get_Content_In_Other_Langauages, {
        fetchPolicy: 'network-only'
    });

    // Load every expert page so the account-wide video count is stable on entry.
    useEffect(() => {
        let cancelled = false;

        const loadAllExperts = async () => {
            const allExperts = [];
            let cursor = null;
            let hasNextPage = true;

            try {
                while (hasNextPage) {
                    const { data } = await fetchExperts({
                        variables: {
                            accountId,
                            pagingCriteria: {
                                limit,
                                cursor,
                                sortByField,
                                sortOrder
                            }
                        }
                    });
                    const result = data?.getAccountExperts;
                    allExperts.push(...(result?.data || []));
                    cursor = result?.pageMeta?.nextPage || null;
                    hasNextPage = Boolean(result?.pageMeta?.hasNextPage && cursor);
                }

                if (!cancelled) {
                    setExperts(allExperts);
                }
            } catch (error) {
                console.error('Error fetching experts:', error);
            }
        };

        loadAllExperts();
        return () => {
            cancelled = true;
        };
    }, [fetchExperts, accountId]);

    useEffect(() => {
        if (experts.length > 0) {
            experts.forEach((expert) => {
                const expertId = expert.id;
                if (!expertVideos[expertId]) {
                    loadVideosForExpert(expertId);
                }
            });
        }
    }, [experts]);

    const loadVideosForExpert = async (expertId, cursor = null, append = false) => {
        setExpertPagination(prev => ({
            ...prev,
            [expertId]: {
                ...(prev[expertId] || {}),
                loading: true
            }
        }));

        try {
            const res = await getExpertVideo({
                variables: {
                    userId: expertId,
                    accountId: accountId,
                    pagingCriteria: {
                        limit,
                        cursor,
                        sortByField,
                        sortOrder
                    }
                }
            });

            const newVideos = res.data.getUserAllContributions?.data || [];
            const pageMeta = res.data.getUserAllContributions?.pageMeta || {};
            const reportedTotal = pageMeta.totalCount == null
                ? Number(experts.find(expert => String(expert.id) === String(expertId))?.videosCount)
                : Number(pageMeta.totalCount);
            setExpertVideoCounts(prev => ({
                ...prev,
                [expertId]: Number.isFinite(reportedTotal) ? reportedTotal : newVideos.length
            }));

            setExpertVideos(prev => ({
                ...prev,
                [expertId]: append
                    ? [...(prev[expertId] || []), ...newVideos]
                    : newVideos
            }));

            setExpertPagination(prev => ({
                ...prev,
                [expertId]: {
                    nextCursor: pageMeta.nextPage,
                    prevCursor: pageMeta.previousPage,
                    hasNextPage: pageMeta.hasNextPage,
                    hasPreviousPage: pageMeta.hasPreviousPage,
                    loading: false
                }
            }));
        } catch (error) {
            console.error("Error loading videos:", error);
            const fallbackCount = Number(experts.find(expert => String(expert.id) === String(expertId))?.videosCount);
            setExpertVideoCounts(prev => ({
                ...prev,
                [expertId]: Number.isFinite(fallbackCount) ? fallbackCount : 0
            }));
            setExpertPagination(prev => ({
                ...prev,
                [expertId]: {
                    ...(prev[expertId] || {}),
                    loading: false
                }
            }));
        }
    };

    const handleMenuClick = async (e, videoId) => {
        setActiveDropdown(null);
        try {
            let expert = null;
            let expertId = null;
            let videos = null;
            let clickedVideo = null;

            for (const [expId, expVideos] of Object.entries(expertVideos)) {
                const foundVideo = expVideos.find(v => String(v.id) === String(videoId));
                if (foundVideo) {
                    expertId = expId;
                    videos = expVideos;
                    expert = experts.find(ex => String(ex.id) === String(expId));
                    clickedVideo = foundVideo;
                    break;
                }
            }

            if (!videos || !expert || !clickedVideo) {
                console.error('Videos or expert not found');
                navigate('/add-content-library', {
                    state: {
                        isEdit: true,
                        editData: {
                            videoId,
                            accountId,
                            roleId,
                            accountName,
                            name,
                            otherLanguagesContent: []
                        },
                        accountId,
                        roleId,
                        accountName,
                        name
                    }
                });
                return;
            }

            const stars = videos.map(video => {
                let quizQuestion = '';
                let quizOptionA = '';
                let quizOptionB = '';
                let correctAnswer = '';

                if (video.quizDetails && video.quizDetails.answer && video.quizDetails.answer.length >= 2) {
                    quizQuestion = video.quizDetails.question || '';

                    const sortedAnswers = [...video.quizDetails.answer].sort((a, b) => a.id - b.id);

                    quizOptionA = sortedAnswers[0]?.optionsAnswer || '';
                    quizOptionB = sortedAnswers[1]?.optionsAnswer || '';

                    const correctAnswerObj = video.quizDetails.answer.find(ans => ans.isanswer === true);
                    if (correctAnswerObj) {
                        if (correctAnswerObj.id === sortedAnswers[0].id) {
                            correctAnswer = 'A';
                        } else if (correctAnswerObj.id === sortedAnswers[1].id) {
                            correctAnswer = 'B';
                        } else {
                            correctAnswer = correctAnswerObj.optionsAnswer === quizOptionA ? 'A' : 'B';
                        }
                    }
                }

                return {
                    videoId: video.id,
                    accountId,
                    roleId,
                    userId: expertId,
                    name: expert.name,
                    employeeId: expert.employeeId || 'No ID',
                    youtubeCode: video.code || '',
                    videoTitle: video.title || '',
                    videoThumbnailCode: video.vimeoThumbnailUrl || '',
                    duration: formatDuration(video.videoLengthMins || 0, video.videoLengthSecs || 0),
                    videoLanguage: video.languageId || video.language || '',
                    originalContent: video.originalContent || false,
                    videoDescription: video.description || '',
                    keyPoints: video.keyPoints?.length ? video.keyPoints.map(kp => ({
                        id: kp.id,
                        keyPoint: kp.keyPoint,
                        contentId: kp.contentId
                    })) : [],
                    quizQuestion: quizQuestion,
                    quizOptionA: quizOptionA,
                    quizOptionB: quizOptionB,
                    correctAnswer: correctAnswer,
                    activityId: video.activityId || '',
                    activityName: video.activityName || '',
                    taskId: video.taskId || '',
                    taskName: video.taskName || '',
                    subTaskId: video.subtaskId || '',
                    subTaskName: video.subtaskName || '',
                    format: video.fourmatCategory || '',
                    commitmentNeeded: video.commitmentNeeded || false,
                    contentId: parseInt(video.id) || 0
                };
            });

            const sortedStars = [
                stars.find(s => String(s.videoId) === String(videoId)),
                ...stars.filter(s => String(s.videoId) !== String(videoId))
            ].filter(Boolean);

            let otherLanguagesContent = [];
            try {
                const { data: otherLanguagesData } = await getContentInOtherLanguages({
                    variables: { contentId: parseInt(videoId) }
                });
                const relatedContent = (otherLanguagesData?.getContentInOtherLangauages || []).map(item => ({
                    ...item,
                    videoId: item.id
                }));
                otherLanguagesContent = [...otherLanguagesContent, ...relatedContent];
            } catch (langError) {
                console.error(`Error fetching other languages for video ${videoId}:`, langError);
            }

            const videoIdsWithOtherContent = new Set(otherLanguagesContent.map(item => String(item.videoId)));
            if (!videoIdsWithOtherContent.has(String(videoId))) {
                otherLanguagesContent.push({
                    ...clickedVideo,
                    videoId: clickedVideo.id
                });
            }

            navigate('/add-content-library', {
                state: {
                    isEdit: true,
                    editData: {
                        stars: sortedStars,
                        accountId,
                        roleId,
                        accountName,
                        name,
                        otherLanguagesContent
                    },
                    accountId,
                    roleId,
                    accountName,
                    name
                }
            });

        } catch (error) {
            console.error('Error in handleMenuClick:', error);
            navigate('/add-content-library', {
                state: {
                    isEdit: true,
                    editData: {
                        videoId,
                        accountId,
                        roleId,
                        accountName,
                        name,
                        otherLanguagesContent: []
                    },
                    accountId,
                    roleId,
                    accountName,
                    name
                }
            });
        }
    };

    const menuItems = (videoId) => (
        <Menu onClick={(e) => handleMenuClick(e, videoId)}>
            <Menu.Item key="edit" icon={<img src={editIcon} alt="" style={{ width: 14, height: 14, objectFit: 'contain' }} />}>Edit</Menu.Item>
            <Menu.Item key="delete">Delete</Menu.Item>
            <Menu.Item key="share">Share</Menu.Item>
        </Menu>
    );

    const filterVideosBySearch = (videos) => {
        if (!searchText.trim()) return videos;

        return videos.filter(video =>
            video.title?.toLowerCase().includes(searchText.toLowerCase()) ||
            video.description?.toLowerCase().includes(searchText.toLowerCase()) ||
            video.language?.toLowerCase().includes(searchText.toLowerCase()) ||
            video.fourmatCategory?.toLowerCase().includes(searchText.toLowerCase())
        );
    };

    const renderExpertContentSections = () => {
        return experts.map(expert => {
            const expertId = expert.id;
            const expertVideosData = expertVideos[expertId] || [];
            const filteredVideos = filterVideosBySearch(expertVideosData);
            if (!searchText.trim() && expertVideosData.length === 0) {
                return null;
            }
            const isLoading = expertPagination[expertId]?.loading;

            return (
                <div className="content-section" key={expertId}>
                    <div className="section-header">
                        <h2 className="section-title">{`${expert.name}'s content`}</h2>
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: "-1%" }}>
                            <span
                                className="moreVideosHeading"
                                onClick={() =>
                                    navigate('/more-videos', {
                                        state: {
                                            expertId: expert.id,
                                            accountId,
                                            accountName,
                                            name,
                                            roleId,
                                            expertName: expert.name,
                                            totalVideos: expertVideoCounts[expert.id] ?? expertVideosData.length,
                                        },
                                    })
                                }
                            >
                                More Videos
                            </span>
                        </div>
                    </div>
                    <div className="video-grid">
                        {isLoading ? (
                            <div className="loading-container">
                                <div className="spinner"></div>
                                <p>Loading videos...</p>
                            </div>
                        ) : filteredVideos.length > 0 ? (
                            filteredVideos.map(video => (
                                <VideoCard
                                    key={video.id}
                                    video={video}
                                    expert={expert}
                                    menuItems={menuItems}
                                    activeDropdown={activeDropdown}
                                    setActiveDropdown={setActiveDropdown}
                                />
                            ))
                        ) : searchText.trim() ? (
                            <div style={{ gridColumn: '1 / 15', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 0' }}>
                                <Empty description="No videos found" />
                            </div>
                        ) : null}
                    </div>
                </div>
            );
        });
    };

    return (

        <div className="content-library">
            <h1 className="library-title">Content Library</h1>
            <div className="search-add-container-content-library">
                <Input
                    placeholder="Search content..."
                    prefix={<SearchOutlined style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }} />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="search-input"
                />
                <button
                    className="add-content-button"
                    onClick={() => {
                        navigate('/add-content-library', {
                            state: { roleId: roleId, accountId: accountId, accountName: accountName, name: name }
                        });
                    }}
                >
                    Add Content
                </button>
            </div>
            <div className="total-video-count">
                Total Videos: <span>{isVideoCountLoading ? '...' : totalVideosCount}</span>
            </div>
            <Card>
                {expertsLoading && experts.length === 0 ? (
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading experts...</p>
                    </div>
                ) : expertsError ? (
                    <div className="error-container">
                        <p>Error loading experts. Please try again later.</p>
                    </div>
                ) : experts.length > 0 ? (
                    renderExpertContentSections()
                ) : (
                    <Empty description="No experts found" />
                )}
            </Card>
        </div>
    );
};

export default ContentLibrary;